// src/hooks/socketService.tsx - DROP-IN REPLACEMENT
import { io, Socket } from "socket.io-client";
import { SOCKET_API_URL } from "../config/config";
import logger, { Logger } from '../utils/logger';

interface ServerToClientEvents {
  message: (data: {
    createdAt: string;
    message: {
      from: string;
      id: string;
      timestamp: string;
      text: { body: string };
      type: string;
    };
    username: string;
  }) => void;
  roomData: (data: {
    room: string;
    users: { id: string; username: string; room: string }[];
  }) => void;
}

interface ClientToServerEvents {
  join: (
    options: { username: string; room: string },
    callback: (error?: string) => void
  ) => void;
  sendMessage: (message: string, callback: () => void) => void;
  "leave-room": (callback: () => void) => void;
}

// **SOLUTION: Lazy initialization - no immediate connection**
let socket: Socket<any> | null = null;
let isConnecting = false;

// **SOLUTION: Queue callbacks until socket is ready**
const pendingCallbacks: Array<{ event: string; callback: Function }> = [];

// **Function to get socket only when needed**
const getSocket = (): Socket<any> => {
  if (socket) {
    return socket;
  }

  if (!socket) {
    logger.log("🔌 Creating socket connection (lazy initialization)");

    const token = localStorage.getItem("auth_token");
    Logger.log(">>>", token?.split(" ")[1]);

    socket = io(SOCKET_API_URL, {
      reconnectionDelayMax: 10000,
      autoConnect: false, // **KEY FIX: Prevent auto-connection**
      query: {
        token: token?.split(" ")[1] || "",
      },
      transports: ["websocket"],
      forceNew: true, // Force a new connection each time
      timeout: 20000, // 20 second timeout
    });

    socket.on("connect", () => {
      logger.log("✅ Socket connected successfully!");
      logger.log("🔌 Socket ID:", socket?.id);
      logger.log("🔌 Socket connected state:", socket?.connected);
      isConnecting = false;
      Logger.log("Connected to Socket.IO server");
    });

    socket.on("connect_error", (error) => {
      console.error("Connection Error: ", error);
      isConnecting = false;
    });

    socket.io.on("error", (error) => {
      Logger.log("Socket error: ", error);
      isConnecting = false;
    });

    socket.on("disconnect", (reason) => {
      console.error("Disconnected: ", reason);
      isConnecting = false;
    });

    // **SOLUTION: Register all pending callbacks when socket is created**
    logger.log(`🔌 Found ${pendingCallbacks.length} queued callbacks to register`);
    pendingCallbacks.forEach(({ event, callback }, index) => {
      logger.log(`🔌 Registering queued callback ${index + 1}/${pendingCallbacks.length} for event: ${event}`);
      if (event === "message") {
        socket!.on(event, (data: any) => {
          logger.log("📨 MESSAGE RECEIVED (from queued callback):", data);
          callback(data);
        });
      } else {
        socket!.on(event, callback);
      }
    });
    logger.log(`✅ All ${pendingCallbacks.length} callbacks registered successfully`);
    pendingCallbacks.length = 0; // Clear the queue
  }

  return socket;
};

const disconnectRoom = () => {
  const socketInstance = socket; // Use existing socket if available
  if (socketInstance && socketInstance.connected) {
    socketInstance.emit("leave-room", (error?: string) => {
      Logger.log("Disconnect Error: ", error);
      if (error) {
        console.error("Disconnect Error: ", error);
        alert(error);
      }
    });
  }
};

const joinRoom = (username: string, room: string): void => {
  logger.log("🔌 joinRoom() called with:", { username, room });
  logger.log("🔌 Current socket state:", socket ? "exists" : "null");
  logger.log("🔌 Socket connected:", socket?.connected || false);
  logger.log("🔌 Pending callbacks count:", pendingCallbacks.length);
  
  const socketInstance = getSocket();
  logger.log("🔌 Socket instance obtained, connecting...");
  logger.log("🔌 Socket URL:", SOCKET_API_URL);
  logger.log("🔌 Socket options:", {
    autoConnect: false,
    transports: ["websocket"],
    token: localStorage.getItem("auth_token")?.split(" ")[1]?.substring(0, 20) + "..."
  });
  
  socketInstance.connect();
  logger.log("🔌 connect() called, waiting for connection...");
  
  // Wait a bit for connection to establish before emitting
  setTimeout(() => {
    logger.log("🔌 About to emit 'join' event:", { username, room });
    logger.log("🔌 Socket connected before join:", socketInstance.connected);
    
    socketInstance.emit("join", { username, room }, (error?: string) => {
      logger.log("🔌 JOIN CALLBACK RECEIVED:", { error, room });
      if (error) {
        console.error("❌ Join Error: ", error);
        logger.log("❌ Join failed:", error);
        alert(error);
      } else {
        logger.log("✅ Successfully joined room:", room);
        logger.log("🔌 Room join successful - socket should now receive messages");
      }
    });
    
    // Add timeout to detect if callback never comes
    setTimeout(() => {
      logger.log("⚠️ WARNING: Join callback timeout - server may not be responding to join requests");
    }, 5000);
  }, 500);
};

const sendMessageFun = (message: string): void => {
  const socketInstance = getSocket(); // **This triggers lazy connection**
  socketInstance.emit("sendMessage", message, () => {
    Logger.log("Message sent: ", message);
  });
};

const onMessage = (
  callback: (data: {
    createdAt: string;
    message: {
      from: string;
      id: string;
      timestamp: string;
      text: { body: string };
      type: string;
    };
    username: string;
  }) => void
): void => {
  logger.log("📨 onMessage() called");
  logger.log("🔌 Current socket exists:", !!socket);
  logger.log("🔌 Socket connected:", socket?.connected || false);
  
  if (!socket) {
    logger.log("📨 No socket yet, queuing message callback");
    pendingCallbacks.push({ event: "message", callback });
    logger.log("📨 Queued callbacks count:", pendingCallbacks.length);
    return;
  }
  
  logger.log("📨 Setting up message listener on existing socket");
  socket.on("message", (data: any) => {
    logger.log("📨 MESSAGE RECEIVED:", data);
    callback(data);
  });
};

// **Create a proxy object that maintains the same interface**
const socketProxy = {
  get connected() {
    return socket?.connected || false;
  },
  on: (event: string, callback: any) => {
    logger.log("🔌 socketProxy.on() called for event:", event);
    if (!socket) {
      logger.log("🔌 No socket exists, queuing listener for later");
      pendingCallbacks.push({ event, callback });
      return;
    }
    return socket.on(event, callback);
  },
  emit: (event: string, ...args: any[]) => {
    const socketInstance = getSocket();
    if (!socketInstance.connected) {
      socketInstance.connect();
    }
    return socketInstance.emit(event, ...args);
  },
  disconnect: () => {
    if (socket) {
      return socket.disconnect();
    }
  }
};

// **DEBUG: Manual test function**
const testSocketConnection = () => {
  logger.log("🧪 TESTING: Manual socket connection test");
  
  const testSocket = io(SOCKET_API_URL, {
    reconnectionDelayMax: 10000,
    autoConnect: true,
    query: {
      path: "/",
      token: localStorage.getItem("auth_token")?.split(" ")[1] || "",
    },
    transports: ["websocket"],
  });

  testSocket.on("connect", () => {
    logger.log("🧪 TEST: Socket connected successfully!");
    logger.log("🧪 TEST: Socket ID:", testSocket.id);
    
    // Test joining a room with timeout detection
    logger.log("🧪 TEST: About to emit join event");
    
    testSocket.emit("join", { username: "test-user", room: "test-room" }, (error?: string) => {
      logger.log("🧪 TEST: Join callback received:", { error });
      if (error) {
        logger.log("🧪 TEST: Join failed:", error);
      } else {
        logger.log("🧪 TEST: Successfully joined test room");
        
        // Test message listener
        testSocket.on("message", (data: any) => {
          logger.log("🧪 TEST: Received message:", data);
        });
        
        // Send a test message
        setTimeout(() => {
          logger.log("🧪 TEST: Sending test message");
          testSocket.emit("sendMessage", "Test message from client", () => {
            logger.log("🧪 TEST: Test message sent callback received");
          });
        }, 1000);
      }
    });
    
    // Timeout detection for join
    setTimeout(() => {
      logger.log("🧪 TEST: Join callback timeout - server not responding to join");
    }, 3000);
  });

  testSocket.on("connect_error", (error: any) => {
    logger.log("🧪 TEST: Connection error:", error);
  });

  testSocket.on("disconnect", (reason: any) => {
    logger.log("🧪 TEST: Disconnected:", reason);
  });

  // Auto-disconnect after 10 seconds
  setTimeout(() => {
    testSocket.disconnect();
    logger.log("🧪 TEST: Test completed, socket disconnected");
  }, 10000);
  
  return testSocket;
};

// Make test function available globally for debugging
(window as any).testSocket = testSocketConnection;

// Also expose debugging functions
(window as any).debugSocket = () => {
  logger.log("🔍 DEBUG: Current socket state:", {
    exists: !!socket,
    connected: socket?.connected,
    pendingCallbacks: pendingCallbacks.length,
    socketId: socket?.id
  });
};

export { 
  socketProxy as socket, 
  joinRoom, 
  sendMessageFun, 
  onMessage, 
  disconnectRoom,
  testSocketConnection
};

logger.log("🔌 SocketService loaded - NO auto-connection (lazy initialization)");